import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@tanstack/react-query';
import { useRuntimeConfig } from '@/config/useRuntimeConfig';
import { useAuth } from '@/auth/useAuth';
import { Button, Callout, FormField, SectionCard, Select } from '@/components/ui';
import { syncAppOrganizations, type OrganizationSyncError } from '@/api/console';
import { useRootOrganizationOptions } from '../accounts/shared/useOrgLevelPicker';
import { describeSyncError } from '../apps/syncErrorMessages';
import type { AppDetailPageVM } from './useAppDetailPage';

// EAA-1878: per-row error identifier is EITHER `organization_id` OR
// `school_id`, never both — same helper `SyncOrganizationsModal` already uses.
function describeErrorRow(row: OrganizationSyncError): string {
    if (row.organization_id !== undefined) return `organization_id=${row.organization_id}`;
    if (row.school_id !== undefined) return `school_id=${row.school_id}`;
    return '';
}

/**
 * EAA-1817 (frame 54, "Tổ chức" tab): the app's org/school tree already
 * synced into the portal.
 *
 * KNOWN GAP (SRS #11): there is no read/list endpoint for the `Organization`
 * rows a given app's sync produced — `OrganizationSyncService` only exposes a
 * write (`POST /admin/apps/{code}/sync-organizations`) and `Organization`
 * itself carries no app-attribution field a list could filter on (confirmed
 * against `directory/models.py` — no `source_system`/external-code column).
 * Per house convention this ships the real data instead of a fabricated
 * table: the sync CONDITIONS, a self-contained "run it, see the result" card
 * mirroring `SyncOrganizationsModal`'s own stats (this session's run only —
 * nothing persists a "last run" summary across page loads either), and a
 * link to `/console/hierarchy`, where every synced `Organization` row really
 * does show up (just not filterable by which app created it).
 *
 * EAA-1887: the sync card also offers an optional root-Organization picker —
 * same `useRootOrganizationOptions` and behaviour as `SyncOrganizationsModal`.
 */
export function AppDetailOrganizationsTab({ vm }: { vm: AppDetailPageVM }) {
    const { t } = useTranslation();
    const config = useRuntimeConfig();
    const { accessToken } = useAuth();
    const app = vm.app;
    const [rootOrganizationId, setRootOrganizationId] = useState<string | undefined>(undefined);
    // EAA-1887: same root-Organization picker as `SyncOrganizationsModal` —
    // see that component's docstring for what selecting one does.
    const rootOrgs = useRootOrganizationOptions(Boolean(app));

    const syncMutation = useMutation({
        mutationFn: () =>
            syncAppOrganizations(config.apiBase, accessToken as string, vm.code, rootOrganizationId),
    });

    if (!app) return null;

    // EAA-1884: `is_internal` gate mirrors `AppDetailPage`'s own
    // `onSyncOrganizations` render-site combination — ACH's org/tenant link
    // is wired through a separate, pre-existing mechanism, so it never
    // implements the org-sync receiver contract.
    const canRun = vm.canSyncOrganizations && Boolean(app.supports_org_sync) && !app.is_internal;

    return (
        <div className="app-detail-grid app-detail-organizations-tab">
            <div className="app-detail-grid__main">
                <SectionCard title={t('console.appDetail.organizations.conditionsCardTitle')}>
                    <dl className="app-detail-fieldlist">
                        <div className="app-detail-fieldlist__row">
                            <dt>{t('console.appDetail.organizations.conditionSupports')}</dt>
                            <dd>
                                {app.supports_org_sync
                                    ? t('console.appDetail.organizations.conditionYes')
                                    : t('console.appDetail.organizations.conditionNo')}
                            </dd>
                        </div>
                        <div className="app-detail-fieldlist__row">
                            <dt>{t('console.appDetail.organizations.conditionNotInternal')}</dt>
                            <dd>
                                {!app.is_internal
                                    ? t('console.appDetail.organizations.conditionYes')
                                    : t('console.appDetail.organizations.conditionNo')}
                            </dd>
                        </div>
                    </dl>
                    <Callout tone="info" label={t('console.appDetail.organizations.orderNoteLabel')}>
                        {t('console.appDetail.organizations.orderNoteBody')}
                    </Callout>
                    <Callout tone="warning" label={t('console.appDetail.organizations.disableNoteLabel')}>
                        {t('console.appDetail.organizations.disableNoteBody')}
                    </Callout>
                    <p className="app-detail-hint">
                        <Link to="/console/hierarchy">
                            {t('console.appDetail.organizations.viewHierarchyLink')}
                        </Link>
                    </p>
                </SectionCard>
            </div>

            <div className="app-detail-grid__side">
                <SectionCard title={t('console.appDetail.organizations.syncCardTitle')}>
                    {!canRun && (
                        <p className="app-detail-hint">
                            {t('console.appDetail.organizations.syncUnavailable')}
                        </p>
                    )}
                    {canRun && (
                        <>
                            <p className="app-detail-hint">
                                {t('console.appDetail.organizations.syncIntro', { name: app.name })}
                            </p>
                            <FormField
                                label={t('console.appDetail.organizations.rootOrgLabel')}
                                hint={t('console.appDetail.organizations.rootOrgHint')}
                            >
                                <Select
                                    value={rootOrganizationId}
                                    onChange={(next) => setRootOrganizationId((next as string) ?? undefined)}
                                    options={rootOrgs.options}
                                    loading={rootOrgs.loading}
                                    disabled={syncMutation.isPending}
                                    allowClear
                                    showSearch
                                    optionFilterProp="label"
                                    placeholder={t('console.appDetail.organizations.rootOrgPlaceholder')}
                                    style={{ width: '100%' }}
                                />
                            </FormField>
                            <div className="app-detail-button-row">
                                <Button onClick={() => syncMutation.mutate()} disabled={syncMutation.isPending}>
                                    {syncMutation.isPending
                                        ? t('console.appDetail.organizations.syncRunning')
                                        : syncMutation.data
                                          ? t('console.appDetail.organizations.syncRerun')
                                          : t('console.appDetail.organizations.syncRun')}
                                </Button>
                            </div>

                            {syncMutation.isError && (
                                <p className="portal-admin-error">
                                    {t('console.appDetail.organizations.syncError')}
                                </p>
                            )}

                            {syncMutation.data && (
                                <>
                                    <div className="app-detail-stat-row">
                                        <div className="app-detail-stat-tile">
                                            <span className="app-detail-stat-tile__label">
                                                {t('console.appDetail.organizations.statCreated')}
                                            </span>
                                            <span className="app-detail-stat-tile__value">
                                                {syncMutation.data.created}
                                            </span>
                                        </div>
                                        <div className="app-detail-stat-tile">
                                            <span className="app-detail-stat-tile__label">
                                                {t('console.appDetail.organizations.statUpdated')}
                                            </span>
                                            <span className="app-detail-stat-tile__value">
                                                {syncMutation.data.updated}
                                            </span>
                                        </div>
                                        <div className="app-detail-stat-tile">
                                            <span className="app-detail-stat-tile__label">
                                                {t('console.appDetail.organizations.statDeactivated')}
                                            </span>
                                            <span className="app-detail-stat-tile__value">
                                                {syncMutation.data.deactivated}
                                            </span>
                                        </div>
                                        <div className="app-detail-stat-tile">
                                            <span className="app-detail-stat-tile__label">
                                                {t('console.appDetail.organizations.statErrors')}
                                            </span>
                                            <span className="app-detail-stat-tile__value is-warning">
                                                {syncMutation.data.errors.length}
                                            </span>
                                        </div>
                                    </div>

                                    {syncMutation.data.errors.length > 0 && (
                                        <ul className="app-detail-activity-list">
                                            {syncMutation.data.errors.map((row, index) => {
                                                const display = describeSyncError(row, t);
                                                return (
                                                    <li key={`${describeErrorRow(row)}-${index}`}>
                                                        <span className="app-detail-activity-list__body">
                                                            <strong>{describeErrorRow(row)}</strong>
                                                            <span>{display.text}</span>
                                                        </span>
                                                    </li>
                                                );
                                            })}
                                        </ul>
                                    )}
                                </>
                            )}
                        </>
                    )}
                </SectionCard>
            </div>
        </div>
    );
}

export default AppDetailOrganizationsTab;
